import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import {
  CompositeFilterDescriptor,
  filterBy,
  FilterDescriptor,
  groupBy,
  GroupDescriptor,
} from '@progress/kendo-data-query';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
  WindowService,
} from '@ups/xplat/core';

import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import { UntypedFormGroup } from '@angular/forms';
import { parseData } from '@ups/xplat/utils';
import {
  IEquipment,
  IEquipmentHistory,
  IFormattedEquipmentHistory,
  IGridGroup,
  IInventoryCount,
  IInventoryCountDetail,
  IInventoryCountStatusChangeAction,
  IInventoryItem,
  IInventoryTemplateItemGroup,
  IInventoryUniqueItem,
  IItemTransfer,
  IManufacturer,
  IVendor,
} from '../models';
import { InventoryService } from './inventory.service';

const ADD_UNIQUE_ITEM_ENDPOINT = (countDetailId: string) =>
  `/api/InventoryCount/countDetail/uniqueItem/${countDetailId}`;
const COUNT_DETAIL_ENDPOINT = (countId: string) =>
  `${COUNTS_ENDPOINT}/${countId}/countDetail`;
const COUNT_ENDPOINT = (countId) => `/api/inventoryCount/${countId}`;
const COUNTS_ENDPOINT = `/api/inventoryCount`;
const COUNT_HISTORY_ENDPOINT = (countId: string) =>
  `${COUNTS_ENDPOINT}/${countId}/history`;
const CREATE_COUNT_DETAIL = (itemId) =>
  `/api/InventoryCount/countDetail/${itemId}`;
const GET_COUNT_UPDATE_ENDPOINT = (countId) => `${COUNTS_ENDPOINT}/${countId}`;
const GET_EQUIPMENT_WITH_INVENTORY_TEMPLATE = `api/InventoryTemplate/equipment`;
const GET_INVENTORY_COUNTS_DETAILS_FOR_EQUIPMENT = (inventoryCountId: string) =>
  `/api/inventoryCount/countDetails/${inventoryCountId}`;
const GET_INVENTORY_COUNTS_FOR_EQUIPMENT = (equipmentKeyId: number) =>
  `${COUNTS_ENDPOINT}/${equipmentKeyId}`;
const COUNT_STATUS_ENDPOINT = (
  countId,
  status: IInventoryCountStatusChangeAction
) => `${COUNTS_ENDPOINT}/${countId}/status/${status}`;
const GET_UNIQUE_ITEMS_ENDPOINT = (itemId: string) =>
  `${UNIQUE_ITEM_ENDPOINT}/${itemId}`;
const MANUFACTURERS_ENDPOINT = `api/Item/manufacturers`;
const MISSING_GROUPS_COUNT_DETAILS = (countId, itemGroupId) =>
  `${COUNT_ENDPOINT(countId)}/itemGroup/${itemGroupId}/missing`;
const REMOVE_UNIQUE_ITEM_ENDPOINT = (
  countDetailId: string,
  uniqueItemId: string
) =>
  `/api/InventoryCount/countDetail/${countDetailId}/uniqueItem/${uniqueItemId}`;
const UNIQUE_ITEM_DETAIL = (uniqueItemId: string) =>
  `/api/Item/uniqueItem/${uniqueItemId}`;
const UNIQUE_ITEM_ENDPOINT = `/api/Item/uniqueItem`;
const UNIQUE_ITEMS_EXPIRING = (
  inventoryCountId: string,
  periodInMonths: number
) => `/api/Item/uniqueItem/${inventoryCountId}/${periodInMonths}`;
const VENDORS_ENDPOINT = `api/Item/vendors`;
const CAN_ADD_UNIQUE_ITEM_TO_DETAIL = (
  countDetailId: string,
  uniqueItemId: string
) =>
  `/api/InventoryCount/countDetail/canAddUniqueItem/${countDetailId}/${uniqueItemId}`;
const PATCH_COUNT_DETAIL = (countId: string, countDetailId: string) =>
  `/api/InventoryCount/${countId}/countDetail/${countDetailId}`;

@Injectable({
  providedIn: 'root',
})
export class InventoryCountService {
  win = inject(WindowService);
  public itemGroups: IInventoryTemplateItemGroup[];
  public manufacturers: IManufacturer[];
  public vendors: IVendor[];
  public countDetailGroups$;

  get countDetailGroups() {
    return this._countDetailGroups.getValue();
  }
  protected toolTrackerBaseUrl: string;
  protected httpToolTracker: HttpClient;

  private countDetailGroupsOriginalValue: IGridGroup<IInventoryCountDetail>[];
  private _countDetailGroups = new BehaviorSubject<
    IGridGroup<IInventoryCountDetail>[]
  >(null);

  constructor(
    httpClientFactory: MyHttpClientFactory,
    private inventoryService: InventoryService
  ) {
    this.toolTrackerBaseUrl = environment.urls.toolTrackerAPI;
    this.httpToolTracker = httpClientFactory.createHttpClient(
      this.toolTrackerBaseUrl,
      true,
      ResponseCasingEnum.CamelCase
    );
    this.inventoryService
      .getItemGroups()
      .subscribe((itemGroups) => (this.itemGroups = itemGroups));
    this.countDetailGroups$ = this._countDetailGroups
      .asObservable()
      .pipe(shareReplay(1));
  }

  getEquipmentCountList(equipmentKeyId: number): Observable<IInventoryCount[]> {
    return this.httpToolTracker.get<IInventoryCount[]>(
      GET_INVENTORY_COUNTS_FOR_EQUIPMENT(equipmentKeyId)
    );
  }

  getEquipmentCountDetails(
    inventoryCountId: string
  ): Observable<IInventoryCountDetail[]> {
    return this.httpToolTracker
      .get<
        IInventoryCountDetail[]
      >(GET_INVENTORY_COUNTS_DETAILS_FOR_EQUIPMENT(inventoryCountId))
      .pipe(
        map((countDetails) =>
          countDetails.map((countDetail) => ({
            ...countDetail,
            item: this.getFormattedItem(countDetail.item),
          }))
        ),
        tap((countDetails) => this.setCountDetailGroups(countDetails))
      );
  }

  updateCount(count: IInventoryCount) {
    const url = GET_COUNT_UPDATE_ENDPOINT(
      count.inventoryCount.inventoryCountId
    );

    return this.httpToolTracker
      .put<IInventoryCount>(url, count)
      .pipe(this.successNotification);
  }

  updateCountDetails(countId: string, detail: IInventoryCountDetail) {
    const url = COUNT_DETAIL_ENDPOINT(countId);
    return this.httpToolTracker
      .post<IInventoryCountDetail>(url, detail)
      .pipe(this.successNotification);
  }

  getEquipmentWithInventoryTemplate(): Observable<IEquipment[]> {
    const url = GET_EQUIPMENT_WITH_INVENTORY_TEMPLATE;

    return this.httpToolTracker.get<IEquipment[]>(url);
  }

  createNewInventoryCount(
    inventoryTemplateId: string,
    equipmentKeyId: number
  ): Observable<IInventoryCount> {
    const url = COUNTS_ENDPOINT;
    const body = {
      inventoryTemplateId: inventoryTemplateId,
      equipmentKeyId: equipmentKeyId,
    };

    return this.httpToolTracker
      .post<IInventoryCount>(url, body)
      .pipe(tap(() => this.win.showToast$.next('Count created')));
  }

  setCountDetailGroups(
    details: IInventoryCountDetail[],
    groupDescriptors: GroupDescriptor[] = [{ field: 'item.itemGroupName' }]
  ): IGridGroup<IInventoryCountDetail>[] {
    const countDetailGroups = groupBy(details, groupDescriptors).map((group) =>
      this.getFormattedItemGroup(group, 'item.itemName')
    );

    this.countDetailGroupsOriginalValue = countDetailGroups;
    this._countDetailGroups.next(countDetailGroups);

    return countDetailGroups;
  }

  resetCountDetailGroups() {
    this._countDetailGroups.next(null);
  }

  getFormattedCountDetailCellFormValue(
    detail: IInventoryCountDetail,
    detailCellForm: UntypedFormGroup,
    inventoryTemplateId?: string
  ) {
    const detailCellFormValue = detailCellForm.value;

    return {
      ...detail,
      ...detailCellFormValue,
      inventoryCountId: detail?.inventoryCountId || inventoryTemplateId,
    };
  }

  updateGridCountDetail(detailToUpdate: IInventoryCountDetail) {
    this.countDetailGroupsOriginalValue = this._updateGridCountDetail(
      detailToUpdate,
      this.countDetailGroupsOriginalValue
    );

    const updatedCountDetailGroups = this._updateGridCountDetail(
      detailToUpdate,
      this.countDetailGroups
    );

    this._countDetailGroups.next(updatedCountDetailGroups);
  }

  getCountHistory(countId: string): Observable<IFormattedEquipmentHistory> {
    const url = COUNT_HISTORY_ENDPOINT(countId);

    return this.httpToolTracker.get<IEquipmentHistory>(url).pipe(
      map((equipmentHistory) => ({
        ...equipmentHistory,
        initialCountTimeToolTip: this.getFormattedInitialCountTime(
          equipmentHistory.initialCountTime
        ),
        mobilizationInitialCountTimeToolTip: this.getFormattedInitialCountTime(
          equipmentHistory.mobilizationInitialCountTime
        ),
      }))
    );
  }

  changeCountStatus(
    count: IInventoryCount,
    status: IInventoryCountStatusChangeAction
  ): Observable<IInventoryCount> {
    let url = COUNT_STATUS_ENDPOINT(count.inventoryCountId, status);

    if (count.inventoryStatusId) {
      url += `?statusId=${count.inventoryStatusId}`;
    }

    return this.httpToolTracker
      .put<IInventoryCount>(url, count)
      .pipe(this.successNotification);
  }

  getUniqueItemsForDetail(query: string, detail: IInventoryCountDetail) {
    const url = GET_UNIQUE_ITEMS_ENDPOINT(detail.item?.itemId);

    return this.httpToolTracker
      .get<IInventoryUniqueItem[]>(url)
      .pipe(
        map((uniqueItems) =>
          uniqueItems.filter((uniqueItem) =>
            uniqueItem.itemIdentifier
              ?.toLocaleLowerCase()
              .includes(query?.toLocaleLowerCase())
          )
        )
      );
  }

  createUniqueItem(item: IInventoryUniqueItem) {
    const url = UNIQUE_ITEM_ENDPOINT;

    return this.httpToolTracker
      .post(url, item)
      .pipe(tap(() => this.win.showToast$.next('Unique item created')));
  }

  editUniqueItem(item: IInventoryUniqueItem) {
    const url = UNIQUE_ITEM_DETAIL(item.uniqueItemId);

    return this.httpToolTracker
      .put(url, item)
      .pipe(tap(() => this.win.showToast$.next('Unique item updated')));
  }

  getExpiringUniqueItems(inventoryCountId: string, periodInMonths: number) {
    const url = UNIQUE_ITEMS_EXPIRING(inventoryCountId, periodInMonths);

    return this.httpToolTracker.get<IInventoryUniqueItem[]>(url);
  }

  canAddUniqueItemToDetail(
    countDetailId: string,
    uniqueItem: IInventoryUniqueItem
  ) {
    const canAddUrl = CAN_ADD_UNIQUE_ITEM_TO_DETAIL(
      countDetailId,
      uniqueItem.uniqueItemId
    );

    return this.httpToolTracker.get<IItemTransfer>(canAddUrl);
  }

  addUniqueItemToDetail(
    countDetailId: string,
    uniqueItem: IInventoryUniqueItem
  ) {
    const url = ADD_UNIQUE_ITEM_ENDPOINT(countDetailId);

    return this.httpToolTracker.post(url, uniqueItem).pipe(
      map((updatedDetail: IInventoryCountDetail) => {
        this.updateGridCountDetail(updatedDetail);

        return updatedDetail;
      }),
      tap(() => this.win.showToast$.next('Unique item added'))
    );
  }

  removeUniqueItemFromDetail(
    detail: IInventoryCountDetail,
    uniqueItem: IInventoryUniqueItem
  ) {
    const url = REMOVE_UNIQUE_ITEM_ENDPOINT(
      detail.inventoryCountDetailId,
      uniqueItem.uniqueItemId
    );

    return this.httpToolTracker
      .delete<IInventoryCountDetail>(url)
      .pipe(tap((updatedDetail) => this.updateGridCountDetail(updatedDetail)));
  }

  getManufacturers(query?: string) {
    const url = MANUFACTURERS_ENDPOINT;

    if (!this.manufacturers) {
      return this.httpToolTracker.get<IManufacturer[]>(url).pipe(
        tap((manufacturers) => (this.manufacturers = manufacturers)),
        map((manufacturers) => this.filterManufacturers(manufacturers, query))
      );
    } else {
      return of(this.filterManufacturers(this.manufacturers, query));
    }
  }

  getVendors(query?: string) {
    const url = VENDORS_ENDPOINT;

    if (!this.vendors) {
      return this.httpToolTracker.get<IVendor[]>(url).pipe(
        tap((vendors) => (this.vendors = vendors)),
        map((vendors) => this.filterVendors(vendors, query))
      );
    } else {
      return of(this.filterVendors(this.vendors, query));
    }
  }

  filterGridDetailGroups(
    filterDescriptor?: FilterDescriptor | CompositeFilterDescriptor,
    filterFunction?: (countDetail: IInventoryCountDetail) => boolean
  ): IGridGroup<IInventoryCountDetail>[] {
    if (!this.countDetailGroups) {
      return;
    }

    if (
      (!filterDescriptor ||
        !(filterDescriptor as CompositeFilterDescriptor)?.filters.length) &&
      !filterFunction
    ) {
      this._countDetailGroups.next(this.countDetailGroupsOriginalValue);
    }

    const filters = filterDescriptor
      ? filterDescriptor
      : {
          field: (countDetail) => countDetail,
          operator: filterFunction,
        };

    const filteredCountDetailGroups = this.countDetailGroupsOriginalValue
      .map((group) => {
        const filteredItems = filterBy(group.items, filters);
        const filteredGroup = {
          ...group,
          items: filteredItems,
          aggregates: this.getGroupAggregates(group),
        };

        return filteredGroup;
      })
      .filter((group) => !!group.items.length);

    this._countDetailGroups.next(filteredCountDetailGroups);

    return filteredCountDetailGroups;
  }

  getExcelExportConfig() {
    return this.countDetailGroups$.pipe(
      take(1),
      map((gridCountDetailGroups) =>
        this.getExportToExcelConfig(gridCountDetailGroups)
      )
    );
  }

  getExcelFileName(
    count: { inventoryStatusName?: string },
    equipment: { name?: string; description?: string }
  ) {
    return `inventory-count-${equipment.name || ''}-${
      equipment.description || ''
    }-${count.inventoryStatusName || ''}-DATE-${new Date().toLocaleDateString(
      'en-US'
    )}.xlsx`;
  }

  getCountMissingGroupDetails(
    countId: string,
    detailsGroupId: string
  ): Observable<IInventoryCountDetail[]> {
    return this.httpToolTracker.get<IInventoryCountDetail[]>(
      MISSING_GROUPS_COUNT_DETAILS(countId, detailsGroupId)
    );
  }

  createCountDetail(itemId: string): Observable<IInventoryCountDetail> {
    const url = CREATE_COUNT_DETAIL(itemId);
    return this.httpToolTracker
      .post<IInventoryCountDetail>(url, null)
      .pipe(map(parseData));
  }

  patchCountDetail(
    countId: string,
    countDetailId: string,
    path: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ): Observable<IInventoryCountDetail> {
    const url = PATCH_COUNT_DETAIL(countId, countDetailId);

    return this.httpToolTracker
      .patch<IInventoryCountDetail>(url, [
        {
          op: 'replace',
          path: `/${path}`,
          value: value,
        },
      ] as const)
      .pipe(map(parseData));
  }

  patchCountDetailItem(
    countId: string,
    countDetailId: string,
    itemId: string
  ): Observable<IInventoryCountDetail> {
    return this.httpToolTracker
      .patch(`/api/InventoryCount/${countId}/${countDetailId}/${itemId}`, [
        {
          op: 'replace',
          path: `/isActive`,
          value: false,
        },
      ])
      .pipe(this.successNotification);
  }

  patchToolNoteDetail(
    countId: string,
    countDetailId: string,
    note: string
  ): Observable<IInventoryCountDetail> {
    const url = PATCH_COUNT_DETAIL(countId, countDetailId);
    return this.httpToolTracker
      .patch<IInventoryCountDetail>(url, [
        {
          op: 'replace',
          path: '/countNotes',
          value: note,
        },
      ] as const)
      .pipe(map(parseData));
  }

  patchProjectManagerData(
    countId: string,
    projectManagerHRRef: number,
    projectManagerName: string
  ): Observable<IInventoryCountDetail> {
    const url = `/api/InventoryCount/${countId}`;
    return this.httpToolTracker
      .patch<IInventoryCountDetail>(url, [
        {
          op: 'replace',
          path: '/projectManagerHRRef',
          value: projectManagerHRRef,
        },
        {
          op: 'replace',
          path: '/projectManagerName',
          value: projectManagerName,
        },
      ] as const)
      .pipe(map(parseData));
  }

  patchWarehouseData(
    countId: string,
    warehouseId: number,
    warehouseName: string
  ): Observable<IInventoryCountDetail> {
    const url = `/api/InventoryCount/${countId}`;
    return this.httpToolTracker
      .patch<IInventoryCountDetail>(url, [
        {
          op: 'replace',
          path: '/warehouseId',
          value: warehouseId,
        },
        {
          op: 'replace',
          path: '/warehouseName',
          value: warehouseName,
        },
      ] as const)
      .pipe(map(parseData));
  }

  patchUniqueItem(
    uniqueItemId: string,
    path: string,
    value: number | string | boolean
  ): Observable<IInventoryCountDetail> {
    const url = `/api/InventoryCount/uniqueItem/${uniqueItemId}`;
    return this.httpToolTracker
      .patch<IInventoryCountDetail>(url, [
        {
          op: 'replace',
          path: `/${path}`,
          value: value,
        },
      ] as const)
      .pipe(map(parseData));
  }

  patchInventoryCountDetailUniqueItem(
    inventoryCountDetailId: string,
    uniqueItemId: string,
    inventoryCountStatusId: string,
    path: string,
    value: number | string | boolean
  ): Observable<IInventoryCountDetail> {
    const url = `/api/InventoryCount/uniqueItem/${inventoryCountDetailId}/${uniqueItemId}/${inventoryCountStatusId}`;
    return this.httpToolTracker
      .patch<IInventoryCountDetail>(url, [
        {
          op: 'replace',
          path: `/${path}`,
          value: value,
        },
      ] as const)
      .pipe(map(parseData));
  }

  async patchCountDetailSync(
    inventoryCountDetailId: string,
    uniqueItemId: string,
    path: string,
    value: number | string | boolean
  ) {
    const update$ = this.patchCountDetail(
      inventoryCountDetailId,
      uniqueItemId,
      path,
      value
    );

    update$.subscribe({
      error: () => {
        // eslint-disable-next-line no-undef
        alert('There was an error updating the item.');
      },
    });

    await firstValueFrom(update$);
  }

  updateCountDetailSortOrder(inventoryCountDetailId: string) {
    return this.httpToolTracker
      .put(
        `/api/InventoryCount/itemGroupSortOrder/${inventoryCountDetailId}`,
        null
      )
      .pipe(map(parseData));
  }

  private getExportToExcelConfig(gridCountDetailGroups) {
    const allDetails = gridCountDetailGroups.reduce(
      (result, current) => [...result, ...current.items],
      []
    );
    const regularDetailGroups = this.getRegularDetailGroups(allDetails);
    const uniqueItemDetailsGroups = this.getUniqueItemDetailsGroups(allDetails);
    const allGroups = uniqueItemDetailsGroups
      ? [...uniqueItemDetailsGroups, ...regularDetailGroups]
      : regularDetailGroups;

    return {
      data: allGroups,
      group: [{ field: 'item.itemGroupName' }, { field: 'item.itemName' }],
    };
  }

  private getRegularDetailGroups(allDetails: IInventoryCountDetail[]) {
    const regularDetails = allDetails.filter(
      (detail) => !detail.item.hasSerialNumber
    );
    const regularDetailGroups = groupBy(regularDetails, [
      { field: 'item.itemGroupName' },
      { field: 'item.itemName' },
    ]).map((group) => ({
      ...group,
      value: '',
      field:
        group.items[0]?.items[0]?.item?.itemGroupName ||
        group.items[0]?.items[0]?.item?.itemGroup?.itemGroupName,
    }));

    return regularDetailGroups;
  }

  private getUniqueItemDetailsGroups(allDetails: IInventoryCountDetail[]) {
    const uniqueItemDetails = allDetails.filter(
      (detail) =>
        detail.item.hasSerialNumber &&
        detail.inventoryCountDetailUniqueItems?.length
    );

    if (!uniqueItemDetails.length) {
      return null;
    }

    const formattedUniqueItemDetails = uniqueItemDetails.reduce(
      (result, uniqueItemDetail) => {
        const uniqueItems =
          uniqueItemDetail.inventoryCountDetailUniqueItems?.map(
            (uniqueItem) => ({
              ...uniqueItem,
              item: {
                itemGroupName:
                  uniqueItemDetail?.item?.itemGroupName ||
                  uniqueItemDetail?.item?.itemGroup?.itemGroupName,
                itemName: uniqueItemDetail.item.itemName,
              },
              initialCount: null,
              initialFieldCount: null,
              finalFieldCount: null,
              mobilizationInitialCount: null,
              detail: uniqueItemDetail,
            })
          );

        if (uniqueItems) {
          return [...result, ...uniqueItems];
        }

        return result;
      },
      []
    );
    const uniqueItemDetailsGroups = groupBy(formattedUniqueItemDetails, [
      { field: 'item.itemGroupName' },
      { field: 'item.itemName' },
    ]).map((uniqueItemDetailGroup) => ({
      ...uniqueItemDetailGroup,
      field:
        uniqueItemDetailGroup.items[0]?.items[0]?.item?.itemGroupName ||
        uniqueItemDetailGroup.items[0]?.items[0]?.item?.itemGroup
          ?.itemGroupName,
      value: '',
      items: uniqueItemDetailGroup.items.map((uniqueItemGroup) => ({
        ...uniqueItemGroup,
        field: uniqueItemGroup.items[0]?.item?.itemName,
        value: '',
        aggregates: uniqueItemGroup.items[0]?.detail,
      })),
    }));

    return uniqueItemDetailsGroups;
  }

  private getFormattedInitialCountTime(minutes: number): string {
    let hour;

    hour = Math.floor(minutes / 60);
    const minute = Math.floor(minutes % 60);
    const day = Math.floor(hour / 24);
    hour = hour % 24;

    const initialCountTime = `
      ${day ? day + ' days, ' : ''}
      ${hour ? hour + ' hours, ' : ''}
      ${minute ? minute + ' minutes ' : ''}
    `;

    return initialCountTime;
  }

  private _updateGridCountDetail(
    detailToUpdate: IInventoryCountDetail,
    countDetailGroups: IGridGroup<IInventoryCountDetail>[]
  ) {
    return countDetailGroups.map((countDetailGroup) => {
      if (
        countDetailGroup.items[0]?.item?.itemGroupId ===
        detailToUpdate.item?.itemGroupId
      ) {
        return {
          ...countDetailGroup,
          items: countDetailGroup.items.map((gridDetail) => {
            if (gridDetail.item.itemId === detailToUpdate.item.itemId) {
              return {
                ...gridDetail,
                ...detailToUpdate,
              };
            } else {
              return gridDetail;
            }
          }),
        };
      } else {
        return countDetailGroup;
      }
    });
  }

  private successNotification = (observable: Observable<never>) =>
    observable.pipe(tap(() => this.win.showToast$.next('Count updated')));

  private getFormattedItemGroup(
    group: IGridGroup<IInventoryCountDetail>,
    groupFilterKey?: string
  ) {
    // We are grouping by item.itemGroupId but, in order the grid column filtering to
    // work properly (<kendo-grid-column field = "item.itemName"> (we want to filter by name)),
    // we need to set the field to item.itemName'
    const formattedGroup = {
      ...group,
      field: groupFilterKey || group.field,
      aggregates: this.getGroupAggregates(group),
    };

    return formattedGroup;
  }

  private getFormattedItem(item: IInventoryItem) {
    const itemsWithNoGroup = 'Items with no group';
    const itemDetailGroupId =
      item?.itemGroupId || item?.itemGroup?.itemGroupId || itemsWithNoGroup;
    const itemGroupName =
      this.itemGroups.find(
        (itemGroup) => itemGroup.itemGroupId === itemDetailGroupId
      )?.itemGroupName || itemsWithNoGroup;

    return {
      ...item,
      itemGroup: {
        ...item.itemGroup,
        itemGroupName: itemGroupName,
      },
    };
  }

  private getGroupAggregates(group: IGridGroup<IInventoryCountDetail>) {
    const aggregates = {
      total: {
        count: group?.items?.length,
      },
    };

    return aggregates;
  }

  private filterManufacturers(manufacturers: IManufacturer[], query: string) {
    if (!query) {
      return manufacturers;
    }

    return manufacturers.filter(
      (manufacturer) =>
        manufacturer.manufacturerId
          ?.toLocaleLowerCase()
          .includes(query?.toLocaleLowerCase()) ||
        manufacturer.manufacturerName
          ?.toLocaleLowerCase()
          .includes(query?.toLocaleLowerCase())
    );
  }

  private filterVendors(vendors: IVendor[], query: string) {
    if (!query) {
      return vendors;
    }

    return vendors.filter(
      (vendor) =>
        vendor.vendorId
          ?.toLocaleLowerCase()
          .includes(query?.toLocaleLowerCase()) ||
        vendor.vendorName
          ?.toLocaleLowerCase()
          .includes(query?.toLocaleLowerCase())
    );
  }
}
